import React, { createContext, useContext, useEffect } from "react";
import { Loading } from "src/components/Feedback/Loading";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useEnv } from "src/services/env";
import * as RD from "src/types/remoteData";

type EligibilityServiceContextValue = {
  enabled: boolean;
};

const EligibilityServiceContext =
  createContext<EligibilityServiceContextValue | null>(null);

type Props = {
  formTemplateRD: RD.RemoteData<unknown, { id: uuid }>;
  children: React.ReactNode;
};

/**
 * Resolves the eligibility service `enabled` flag before rendering children.
 *
 * While the flag/isSupported check is in-flight, a loading indicator is shown.
 * By the time children render, `enabled` is guaranteed to be a resolved boolean.
 * This prevents timing bugs where consumers see `enabled=false` while the
 * check is still pending.
 */
export function EligibilityServiceProvider({
  formTemplateRD,
  children,
}: Props) {
  const flags = useFlags(["eligibility-service"]);
  const env = useEnv();
  const orgPath = useOrganizationPath();
  const [enabled, setEnabled] = React.useState<RD.RemoteData<unknown, boolean>>(
    RD.loading()
  );

  // Derive stable primitives so the dep array doesn't change identity every render.
  const flagEnabled = flags["eligibility-service"].enabled;
  const formTemplateHasError = formTemplateRD.hasError();
  const formTemplateId: uuid | undefined = formTemplateRD.hasData()
    ? formTemplateRD.data.id
    : undefined;

  useEffect(() => {
    if (!flagEnabled) {
      // flag is disabled, disable eligibility service and use frontend eligibility
      setEnabled(RD.success(false));
      return;
    }

    if (formTemplateHasError) {
      // form template has error, but flag is enabled, we shouldn't block children rendering.
      // set enabled to true to use eligibility service.
      setEnabled(RD.success(true));
      return;
    }

    if (formTemplateId === undefined) {
      // form template has no error, but no data is available yet — stay loading
      setEnabled(RD.loading());
      return;
    }

    // Inputs changed and we need to (re-)fetch; show loading while in-flight
    setEnabled(RD.loading());

    let cancelled = false;

    const checkIsSupported = async (id: uuid) => {
      const response = await fetch(
        `${env.VITE_ELIGIBILITY_SERVICE_URL}/organizations/${orgPath}/formTemplates/${id}/isSupported`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const isSupported = await response.json();
      if (typeof isSupported !== "boolean") {
        throw new Error(
          `Unexpected isSupported response: ${JSON.stringify(isSupported)}`
        );
      }
      return isSupported;
    };

    checkIsSupported(formTemplateId)
      .then((supported) => {
        if (!cancelled) {
          setEnabled(RD.success(supported));
        }
      })
      .catch((error) => {
        console.error("Failed to check eligibility engine support", error);
        if (!cancelled) {
          // Fail-open: if flag is enabled, treat as enabled so the eligibility service path is used
          setEnabled(RD.success(true));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    env.VITE_ELIGIBILITY_SERVICE_URL,
    flagEnabled,
    formTemplateHasError,
    formTemplateId,
    orgPath,
  ]);

  if (!enabled.hasData()) {
    return <Loading />;
  }

  return (
    <EligibilityServiceContext.Provider value={{ enabled: enabled.data }}>
      {children}
    </EligibilityServiceContext.Provider>
  );
}

export function useEligibilityEnabled(): boolean {
  const context = useContext(EligibilityServiceContext);
  if (context === null) {
    throw new Error(
      "useEligibilityEnabled must be used within an EligibilityServiceProvider"
    );
  }
  return context.enabled;
}
